import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/system';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import ProfilesTemplateSchemas from '@/shared/types/shipping/profilesTemplateSchemas.type';

import useCustomsDetails from './hooks/useCustomsDetails';

type CustomsDetailsProps = {
  templateSchemas: ProfilesTemplateSchemas;
  customsDetailsState: ReturnType<typeof useCustomsDetails>;
};

const CustomsDetails: FC<CustomsDetailsProps> = ({
  templateSchemas,
  customsDetailsState,
}) => {
  const { t } = useTranslation(['shipping', 'forms']);

  const {
    customsDetailsForm,
    brokerForm,
    formErrors,
    setBrokerForm,
    setCustomsDetailsForm,
  } = customsDetailsState;

  return (
    <Stack py={2} spacing={2} useFlexGap>
      <WaveUIForm_V2
        fields={[
          {
            field_type: FieldType.Select,
            field_props: {
              name: 'source',
              label: t('forms:source'),
              placeholder: t('forms:source'),
              value: customsDetailsForm.source,
              options: templateSchemas.customs_sources.map((item) => {
                const label: Record<string, string> = {
                  'details': 'Details',
                  'in-person': 'In Person',
                };

                return {
                  label: label[item.customs_source],
                  value: item.customs_source,
                };
              }),
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'email_address',
              label: t('forms:email_address'),
              placeholder: t('forms:email_address'),
              value: customsDetailsForm.email_address,
              error: !!formErrors.email_address,
              helperText: formErrors.email_address,
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'usmca_number',
              label: t('forms:usmca_number'),
              placeholder: t('forms:usmca_number'),
              value: customsDetailsForm.usmca_number,
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'fda_number',
              label: t('forms:fda_number'),
              placeholder: t('forms:fda_number'),
              value: customsDetailsForm.fda_number,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Text,
            field_props: {
              name: 'fax_extension',
              label: t('forms:fax_extension'),
              placeholder: t('forms:fax_extension'),
              value: customsDetailsForm.fax_extension,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Text,
            field_props: {
              name: 'fax_number',
              label: t('forms:fax_number'),
              placeholder: t('forms:fax_number'),
              value: customsDetailsForm.fax_number,
            },
          },
        ]}
        getFieldsValues={(value) => {
          setCustomsDetailsForm((prev) => ({ ...prev, ...value }));
        }}
      />

      <WaveUIAccordion defaultExpanded title={t('shipping:broker')}>
        <WaveUIForm_V2
          fields={[
            {
              columns: 6,
              field_type: FieldType.Switch,
              field_props: {
                name: 'use_carrier_as_broker',
                label: t('forms:use_carrier_as_broker'),
                checked: brokerForm.use_carrier_as_broker,
              },
            },
            {
              field_type: FieldType.Text,
              isVisible: !brokerForm.use_carrier_as_broker,
              field_props: {
                name: 'broker_name',
                label: t('forms:broker_name'),
                placeholder: t('forms:broker_name'),
                value: brokerForm.broker_name,
              },
            },
            {
              field_type: FieldType.Text,
              isVisible: !brokerForm.use_carrier_as_broker,
              field_props: {
                name: 'broker_account_number',
                label: t('forms:broker_account_number'),
                placeholder: t('forms:broker_account_number'),
                value: brokerForm.broker_account_number,
              },
            },
            {
              field_type: FieldType.Text,
              isVisible: !brokerForm.use_carrier_as_broker,
              field_props: {
                name: 'broker_extension',
                label: t('forms:broker_extension'),
                placeholder: t('forms:broker_extension'),
                value: brokerForm.broker_extension,
              },
            },
            {
              field_type: FieldType.Text,
              isVisible: !brokerForm.use_carrier_as_broker,
              field_props: {
                name: 'broker_phone_number',
                label: t('forms:broker_phone_number'),
                placeholder: t('forms:broker_phone_number'),
                value: brokerForm.broker_phone_number,
              },
            },
          ]}
          getFieldsValues={(value) => {
            setBrokerForm((prev) => ({ ...prev, ...value }));
          }}
        />
      </WaveUIAccordion>
    </Stack>
  );
};

export default CustomsDetails;
